import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, Button, Card, CardContent, Chip, Link,
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, CircularProgress, Alert, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CampaignIcon from '@mui/icons-material/Campaign';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const API = 'https://portal.peaksoftcon.com:8081/api';

// 'YYYY-MM-DD' → 'DD.MM.YYYY'. Date objesi de gelebilir (mysql driver).
const fmtTRDate = (v) => {
  if (!v) return '—';
  const s = typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
};

// Kaynağa göre renk — ileride başka kaynaklar eklenince ayrışsın.
const sourceColor = (src) => {
  switch ((src || '').toUpperCase()) {
    case 'TÜBİTAK': return 'primary';
    default: return 'default';
  }
};

export default function Announcements() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isAdmin = useMemo(() => {
    try {
      const info = JSON.parse(localStorage.getItem('companyInfo') || 'null');
      return info?.role === 'Admin';
    } catch { return false; }
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/announcements`);
      setRows(data || []);
    } catch (err) {
      console.error('announcements load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/announcements/refresh`);
      setResult({
        severity: data.error ? 'error' : (data.new > 0 ? 'success' : 'info'),
        message: data.error
          ? 'Tarama hatası: ' + data.error
          : data.new > 0
            ? `${data.new} yeni duyuru eklendi ve herkese bildirim gönderildi.`
            : data.seeded
              ? `İlk doldurma yapıldı (${data.total} kayıt, bildirim gönderilmedi).`
              : 'Yeni duyuru yok — liste güncel.',
      });
      await load();
    } catch (err) {
      setResult({
        severity: 'error',
        message: 'Tarama hatası: ' + (err.response?.data?.error || err.message),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.title}" duyurusunu silmek istiyor musun?`)) return;
    try {
      await axios.delete(`${API}/announcements/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      alert('Silme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  // İçerik hücresi: başlık (kalın) + detay satırları + bağlantı.
  const renderContent = (row) => (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {row.title}
      </Typography>
      {row.content && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}
        >
          {row.content}
        </Typography>
      )}
      {row.url && (
        <Link
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontSize: 13 }}
        >
          Detayı aç <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      )}
    </Box>
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <CampaignIcon color="primary" />
          <Typography variant="h4" sx={{ m: 0 }}>Duyurular</Typography>
        </Stack>
        {isAdmin && (
          <Tooltip title="TÜBİTAK Açık Çağrılar'ı şimdi tara">
            <span>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Taranıyor...' : 'Şimdi Tara'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        TÜBİTAK Açık Çağrılar her gün 11:30'da otomatik taranır; yeni bir
        duyuru çıktığında herkese bildirim gönderilir.
      </Typography>

      {result && (
        <Alert severity={result.severity} sx={{ mb: 2 }} onClose={() => setResult(null)}>
          {result.message}
        </Alert>
      )}

      {loading ? (
        <Stack direction="row" justifyContent="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 ? (
        <Paper sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">Henüz duyuru yok.</Typography>
        </Paper>
      ) : isMobile ? (
        // Mobil: kart görünümü
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <Card key={row.id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Chip label={row.source} color={sourceColor(row.source)} size="small" />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {fmtTRDate(row.announced_date)}
                    </Typography>
                    {isAdmin && (
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
                {renderContent(row)}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        // Masaüstü: tablo görünümü
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: 130 }}>Kaynak</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Tarih</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>İçerik</TableCell>
                {isAdmin && <TableCell sx={{ fontWeight: 'bold', width: 60 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Chip label={row.source} color={sourceColor(row.source)} size="small" />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTRDate(row.announced_date)}</TableCell>
                  <TableCell>{renderContent(row)}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
